import { TableProps } from '@round/ui-kit';
import { ColumnDef } from '@tanstack/react-table';
import styles from './PublicReportCampaignTable.module.css';
import CampaignsTable from 'Modules/Plans/Campaigns/components/CampaignsTable/CampaignsTable';
import ExpandCell from 'Modules/Plans/Campaigns/components/cells/ExpandCell/ExpandCell';
import ProjectCell, {
    Meta as ProjectCellMeta,
    ProjectData,
} from 'Modules/Plans/Campaigns/components/cells/ProjectCell/ProjectCell';
import CampaignStatsCell from 'Modules/Plans/Campaigns/components/cells/CampaignStatsCell';
import { CampaignsTableMeta } from 'Modules/Plans/Campaigns/types';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';
import { toDecimalPoint } from '@round/utils';
import DurationCell from 'Modules/Plans/Campaigns/components/cells/DurationCell';
import { numberWithCommas } from 'helpers';
import ReportCampaignCell from 'Modules/Plans/Campaigns/components/CampaignsTable/components/ReportCampaignCell/ReportCampaignCell';
import { CampaignTableRow } from './types';
import PostsCell from './components/PostsCell';
import ProgressCell from './components/ProgressCell';
import CampaignCostCell from './components/CampaignCostCell';
import YoutubeActionsCell from './components/ActionsCell/YoutubeActionsCell';

type Props = Pick<
    TableProps<CampaignTableRow>,
    'data' | 'expandedState' | 'onExpandedChange' | 'renderSubComponent'
> & {
    hasError?: boolean;
    isLoading: boolean;
    meta: Meta;
};

export type Meta = CampaignsTableMeta &
    Pick<ProjectCellMeta<CampaignTableRow>, 'getIsBrandLoading' | 'getIsProjectLoading' | 'getIsTeamLoading'>;

const YoutubePublicReportCampaignsTable = ({
    data,
    isLoading,
    hasError,
    expandedState,
    onExpandedChange,
    renderSubComponent,
    meta,
}: Props) => {
    const columns: ColumnDef<CampaignTableRow, any>[] = [
        {
            header: '',
            id: 'expand',
            cell: ExpandCell,
        },
        {
            header: 'Project',
            id: 'project',
            accessorFn: (row): ProjectData => ({
                name: row.project?.name ?? '-',
                image: row.project?.image,
                brandImage: row.brand?.image,
                brandName: row.brand?.name ?? '-',
                teamName: row.team?.name ?? '-',
            }),
            cell: ProjectCell,
        },
        {
            header: 'Campaign',
            accessorKey: 'name',
            meta: {
                className: styles.campaignColumn,
            },
            cell: ReportCampaignCell,
        },
        {
            header: 'Posts',
            id: 'posts',
            cell: PostsCell,
        },
        {
            header: 'Progress',
            id: 'progress',
            cell: ProgressCell,
        },
        {
            header: 'Cost',
            id: 'cost',
            cell: CampaignCostCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => (row.stats?.view_count ? numberWithCommas(row.stats.view_count) : '-'),
            cell: CampaignStatsCell,
        },
        {
            header: () => (
                <TooltipHeader header="ER" tooltipTitle="Engagement Rate" tooltipBody={tooltipCopy.engagementRate} />
            ),
            id: 'er',
            accessorFn: (row) =>
                typeof row.stats?.engagement_rate === 'number'
                    ? `${toDecimalPoint(row.stats.engagement_rate * 100, 2)}%`
                    : '-',
            cell: CampaignStatsCell,
        },
        {
            header: 'Duration',
            id: 'duration',
            cell: DurationCell,
        },
        {
            header: '',
            id: 'actions',
            meta: { className: styles.actionsCell },
            cell: YoutubeActionsCell,
        },
    ];

    return (
        <CampaignsTable
            className={styles.table}
            data={data}
            columns={columns}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            expandedState={expandedState}
            onExpandedChange={onExpandedChange}
            onRowClick={(row) => {
                if (typeof expandedState === 'object') {
                    onExpandedChange?.({ ...expandedState, [row.id]: !expandedState[row.id] });
                }
            }}
            renderSubComponent={renderSubComponent}
            columnVisibility={{
                cost: data.some((d) => 'cost' in d),
            }}
        />
    );
};

export default YoutubePublicReportCampaignsTable;
